/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import {
  type Campaign,
  type CustomerCampaign,
  type DiscountDefinition,
  fetchCustomerCampaignByLookupKey,
  isCoreSeatDiscount,
} from 'embercom/lib/billing/campaign';
import { BILLING_PERIODS } from 'embercom/lib/billing';
import moment from 'moment-timezone';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type CardlessConversionModalService from 'embercom/services/cardless-conversion-modal-service';
import type ReactTeammateAppIsland from 'embercom/services/react-teammate-app-island';
import {
  CampaignTranslations,
  findTranslationKey,
} from 'embercom/lib/self-serve/campaign-translations';
import { get } from 'embercom/lib/ajax';

export const FREE_SEATS_CAMPAIGN_LOOKUP_KEY = 'campaign_free_seats_offer';

interface FreeSeatsCampaignMetadata {
  automationRate: number | null;
  finIsLive: boolean;
  subscriptionPhase: 'in_trial' | 'within_free_seats_window' | 'past_free_seats_window';
  daysRemainingInBillingCycle: number | null;
}

export default class CompellingOfferCampaignService extends Service {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare cardlessTrialService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare aiAgentSetupService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare cardlessConversionModalService: CardlessConversionModalService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIsland;

  @tracked campaign: Campaign | null = null;
  @tracked automationRate: number | null = null;
  declare campaignTranslations: CampaignTranslations;

  init() {
    super.init();
    this.campaignTranslations = new CampaignTranslations(this);
    this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'open-cardless-conversion-modal',
      () => {
        this.cardlessConversionModalService.toggle();
      },
    );
  }

  @keepLatestTask
  *fetchCampaign(): TaskGenerator<void> {
    if (this.currentCampaign?.lookup_key) {
      this.campaign = yield fetchCustomerCampaignByLookupKey(
        this.appService.app.id,
        this.currentCampaign.lookup_key,
      );
    }
    yield this.fetchAutomationRate();
  }

  private async fetchAutomationRate(): Promise<void> {
    try {
      let response: { automation_rate: number } = await get('/ember/campaigns/automation_rate');
      this.automationRate = response.automation_rate / 100;
    } catch {
      this.automationRate = null;
    }
  }

  get customer() {
    return this.customerService.customer;
  }

  get currentCampaign(): CustomerCampaign | undefined {
    return this.customer?.currentCampaign;
  }

  get redeemableCampaign(): CustomerCampaign | undefined {
    return this.customer?.redeemableCampaign;
  }

  get ongoingCampaign(): CustomerCampaign | undefined {
    return this.customer?.ongoingCampaign;
  }

  discountForPlan(planId: string): DiscountDefinition | undefined {
    return this.campaign?.discount_definitions?.find(
      (dd) => dd.plan_id !== null && dd.plan_id.toString() === planId,
    );
  }

  coreSeatDiscountForPlan(planId: string): DiscountDefinition | undefined {
    return this.campaign?.discount_definitions?.find(
      (dd) => dd.plan_id !== null && dd.plan_id.toString() === planId && isCoreSeatDiscount(dd),
    );
  }

  applyDiscount(basePriceInDollars: number, discount: DiscountDefinition | undefined): number {
    if (!discount) {
      return basePriceInDollars;
    }
    if (discount.discount_type === 'fixed') {
      let discountInDollars = Number((discount.discount_value / 100).toFixed(2));
      return Math.max(0, basePriceInDollars - discountInDollars);
    }
    return basePriceInDollars * (1 - discount.discount_value / 100);
  }

  applyCoreSeatDiscount(planId: string, basePriceInDollars: number): number {
    return this.applyDiscount(basePriceInDollars, this.coreSeatDiscountForPlan(planId));
  }

  get isFreeSeatsCampaign(): boolean {
    return (
      this.currentCampaign?.lookup_key === FREE_SEATS_CAMPAIGN_LOOKUP_KEY &&
      this.isEligibleForCampaignExperience
    );
  }

  get isEligibleForCampaignExperience(): boolean {
    return Boolean(this.customer) && !this.appService.app.isStandaloneApp;
  }

  private isExpired(date: Date | string | null | undefined): boolean {
    if (!date) {
      return false;
    }

    return new Date(date) < new Date();
  }

  get hasSuccessContentTranslation(): boolean {
    return !!findTranslationKey(this.ongoingCampaign, 'successContentDescription');
  }

  /*
    Returns metadata for the free seats campaign, including the current
    subscription phase:
    - in_trial: app is in trial or expired trial
    - within_free_seats_window: app is in their first billing cycle
    - past_free_seats_window: app is past their first billing cycle
  */
  get freeSeatsCampaignMetadata(): FreeSeatsCampaignMetadata {
    let automationRate = this.automationRate;
    let finIsLive = !!this.aiAgentSetupService.isFinLive;

    if (this.cardlessTrialService.inTrialOrExpiredTrial) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'in_trial',
        daysRemainingInBillingCycle: null,
      };
    }

    let customer = this.customerService.customer;
    if (!customer) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'past_free_seats_window',
        daysRemainingInBillingCycle: null,
      };
    }

    let startedAt = customer.subscription?.startedAt;

    if (customer.hasActiveSubscription && startedAt) {
      let endDate = customer.currentBillingCycleEndDate;
      let daysRemainingInBillingCycle = endDate
        ? moment(endDate).diff(moment().startOf('day'), 'days')
        : null;

      let isInFirstBillingCycle =
        customer.currentBillingCycleStartDate &&
        moment(startedAt).format('YYYY-MM-DD') ===
          moment(customer.currentBillingCycleStartDate).format('YYYY-MM-DD');

      if (isInFirstBillingCycle) {
        return {
          automationRate,
          finIsLive,
          subscriptionPhase: 'within_free_seats_window',
          daysRemainingInBillingCycle,
        };
      }

      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'past_free_seats_window',
        daysRemainingInBillingCycle,
      };
    }

    return {
      automationRate,
      finIsLive,
      subscriptionPhase: 'past_free_seats_window',
      daysRemainingInBillingCycle: null,
    };
  }

  canViewGenericCampaignBanners(period: BILLING_PERIODS = BILLING_PERIODS.Monthly): boolean {
    return (
      this.isEligibleForCampaignExperience &&
      !!this.redeemableCampaign &&
      !this.isExpired(this.redeemableCampaign.redemption_expires_at) &&
      period === BILLING_PERIODS.Monthly &&
      !this.isFreeSeatsCampaign
    );
  }

  canViewRedeemedCampaignBanners(): boolean {
    return (
      this.isEligibleForCampaignExperience &&
      !!this.ongoingCampaign &&
      !this.isExpired(this.ongoingCampaign.discount_finishes_at)
    );
  }

  get canShowTrialBanner(): boolean {
    return (
      this.canViewGenericCampaignBanners() &&
      !!this.campaignTranslations.trialBannerText &&
      !!this.campaignTranslations.trialBannerCtaLabel
    );
  }

  canShowCheckoutsBanner(period: BILLING_PERIODS): boolean {
    return (
      this.canViewGenericCampaignBanners(period) && !!this.campaignTranslations.checkoutsBannerText
    );
  }

  get canShowUpgradeModalBanner(): boolean {
    return (
      this.canViewRedeemedCampaignBanners() && !!this.campaignTranslations.upgradeModalBannerText
    );
  }

  get hasActiveCompellingOffer(): boolean {
    return this.canViewRedeemedCampaignBanners() || this.canViewGenericCampaignBanners();
  }
}

declare module '@ember/service' {
  interface Registry {
    compellingOfferCampaignService: CompellingOfferCampaignService;
    'compelling-offer-campaign-service': CompellingOfferCampaignService;
  }
}
